import type { FieldGroupErrorType } from '@/app/components/forms/field-group';
import {
  type CustomFormStyleConfig,
  getCustomFormStyle,
  helpTextStyled,
} from '@/app/components/forms/styles';
import type { Theme } from '@/app/types/styles/theme';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';

export interface FieldGroupMessageType {
  /**
   * The message to display in the field message component.
   */
  message?: string;
  /**
   * If `showMaxLength = true` - Text / Max length will be displayed at the bottom right of the field group
   */
  showMaxLength?: boolean;
  /**
   * Current text length - It will be displayed only when `showMaxLength = true`
   */
  length?: number;
  /**
   * Max text length  - It will be displayed only when `showMaxLength = true`
   */
  maxLength?: number;
  /**
   * Theme styling
   */
  theme?: Theme;
}
interface FieldGroupMessageProps
  extends FieldGroupMessageType,
    FieldGroupErrorType {
  /**
   * Custom theme styling
   */
  customStyle?: CustomFormStyleConfig['fieldGroup'];
}

export const FieldGroupMessage = ({
  message,
  customStyle,
  length = 0,
  maxLength = 0,
  error = false,
  theme = 'light',
  showMaxLength = false,
}: FieldGroupMessageProps) => {
  const countText = `${length}/${maxLength}`;

  const messageClass = cn(
    helpTextStyled({ theme, error }),
    'text-caption transition-color',
    getCustomFormStyle(customStyle?.message, {
      error,
    })
  );

  const shouldShowMessage = !!message;
  const shouldShowCount =
    showMaxLength &&
    typeof length === 'number' &&
    typeof maxLength === 'number';

  if (shouldShowMessage && shouldShowCount) {
    return (
      <div
        className={cn(
          'flex items-baseline gap-4',
          {
            'justify-end': showMaxLength && !message,
            'justify-between': showMaxLength && message,
          },
          messageClass
        )}
      >
        <span {...createDataAttributes({ test: 'message' })}>{message}</span>
        <span {...createDataAttributes({ test: 'count' })}>{countText}</span>
      </div>
    );
  }

  if (shouldShowMessage) {
    return (
      <span
        className={cn('block', messageClass)}
        {...createDataAttributes({ test: 'message' })}
      >
        {message}
      </span>
    );
  }

  if (shouldShowCount) {
    return (
      <span
        className={cn('block text-right', messageClass)}
        {...createDataAttributes({ test: 'count' })}
      >
        {countText}
      </span>
    );
  }
};
